import { useEffect, useState } from 'react'
import { Alert, Select, Stack, Text, Textarea } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { AppModal, ActionButton } from '@/components/ui'
import { api, ApiError } from '@/lib/api'

const STATUS_OPTIONS = [
  { value: 'new', label: 'Новое' },
  { value: 'open', label: 'Открыто' },
  { value: 'in_progress', label: 'В работе' },
  { value: 'pending', label: 'Ожидание' },
  { value: 'resolved', label: 'Решено' },
  { value: 'closed', label: 'Закрыто' },
]

type Props = {
  opened: boolean
  onClose: () => void
  ticketIds: string[]
  onApplied: () => void
}

export function TicketsBulkStatusModal({ opened, onClose, ticketIds, onApplied }: Props) {
  const [status, setStatus] = useState<string | null>('open')
  const [pendingReasons, setPendingReasons] = useState<string[]>([])
  const [pendingReason, setPendingReason] = useState<string | null>(null)
  const [pendingComment, setPendingComment] = useState('')
  const [pendingCommentRequired, setPendingCommentRequired] = useState(true)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!opened) setLoading(false)
  }, [opened])

  useEffect(() => {
    if (!opened) return
    void api
      .workspaceMe()
      .then((ws) => {
        const reasons = Array.isArray(ws.pending_status_reasons)
          ? ws.pending_status_reasons.map((item) => String(item || '').trim()).filter(Boolean)
          : []
        setPendingReasons(reasons)
        setPendingReason((current) => current || reasons[0] || null)
        setPendingCommentRequired(ws.pending_status_comment_required !== false)
      })
      .catch(() => {
        setPendingReasons([])
        setPendingReason(null)
        setPendingCommentRequired(true)
      })
  }, [opened])

  const apply = async () => {
    if (!ticketIds.length) {
      showNotification({ title: 'Нет выбора', message: 'Выберите хотя бы одно обращение.', color: 'yellow' })
      return
    }
    if (!status) return
    if (status === 'pending') {
      if (!pendingReason) {
        showNotification({ title: 'Ожидание', message: 'Выберите причину ожидания.', color: 'yellow' })
        return
      }
      if (pendingCommentRequired && !pendingComment.trim()) {
        showNotification({ title: 'Ожидание', message: 'Напишите комментарий.', color: 'yellow' })
        return
      }
    }
    setLoading(true)
    try {
      const res = await api.ticketsBulkUpdate({
        ticket_ids: ticketIds,
        status,
        pending_reason: status === 'pending' ? pendingReason || undefined : undefined,
        pending_comment: status === 'pending' ? pendingComment.trim() : undefined,
      })
      showNotification({
        title: 'Статус изменён',
        message: `Обновлено обращений: ${res.updated}.`,
        color: 'green',
      })
      onApplied()
      onClose()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось изменить статус',
        color: 'red',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title="Массовое изменение статуса"
      size="md"
      footer={
        <>
          <ActionButton action="cancel" onClick={onClose} disabled={loading} />
          <ActionButton action="apply" onClick={() => void apply()} loading={loading} />
        </>
      }
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Выбрано обращений: {ticketIds.length}
        </Text>
        <Select
          label="Новый статус"
          data={STATUS_OPTIONS}
          value={status}
          onChange={(value) => {
            setStatus(value)
            if (value === 'pending') {
              setPendingReason((current) => current || pendingReasons[0] || null)
            }
          }}
          disabled={loading}
        />
        {status === 'pending' ? (
          <Stack gap="sm">
            {pendingReasons.length === 0 ? (
              <Alert color="yellow" variant="light">
                В настройках рабочего пространства не заполнен список причин ожидания.
              </Alert>
            ) : null}
            <Select
              label="Причина ожидания"
              data={pendingReasons.map((reason) => ({ value: reason, label: reason }))}
              value={pendingReason}
              onChange={setPendingReason}
              searchable
              required
              disabled={loading || pendingReasons.length === 0}
            />
            <Textarea
              label="Комментарий"
              description={pendingCommentRequired ? 'Обязателен' : 'Необязательно'}
              value={pendingComment}
              onChange={(e) => setPendingComment(e.currentTarget.value)}
              minRows={3}
              maxLength={2000}
              required={pendingCommentRequired}
              disabled={loading}
            />
          </Stack>
        ) : null}
      </Stack>
    </AppModal>
  )
}
